import type { FeedType } from '@kanbu/schema';
import { useLingui } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';
import { getQueryKey } from '@trpc/react-query';
import { Button } from '@utima/ui';
import { Link2, PlusCircle } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { TablePage } from '@/components/page/TablePage';
import { AddExistingFeedDialog } from '@/features/feeds/AddExistingFeedDialog';
import { FeedSettingsModal } from '@/features/feeds/FeedSettingsModal';
import { useFeedsCols } from '@/features/feeds/useFeedsCols';
import { ProductStatsSummary } from '@/features/shopProducts/components/ProductStatsSummary';
import { useDetailRowHandler } from '@/hooks/useDetailRowHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { trpc, trpcClient } from '@/services/trpc';

// Feeds-per-chat is assumed small (D10) — a single page covers the aggregate
// sum without a dedicated backend aggregate endpoint.
const AGGREGATE_STATS_PAGE_SIZE = 100;

export const Route = createFileRoute('/_baseLayout/chats/$chatId/feeds/')({
  component: FeedsPage,
  loader: async ({ context: { trpcUtils }, params: { chatId } }) =>
    trpcUtils.chats.findOne.ensureData({ id: chatId }),
});

export default function FeedsPage() {
  const { chatId } = Route.useParams();
  const { t } = useLingui();
  const [data] = trpc.chats.findOne.useSuspenseQuery({ id: chatId });

  const { data: allFeeds } = trpc.feeds.findAll.useQuery(
    {
      where: { chats: chatId },
      populate: [
        'pendingProductsCount',
        'embeddingProductsCount',
        'doneProductsCount',
        'failedProductsCount',
      ],
      pagination: { pageSize: AGGREGATE_STATS_PAGE_SIZE },
    },
    { refetchInterval: 5000 },
  );

  const aggregateCounts = useMemo(() => {
    const items = allFeeds?.items ?? [];

    return items.reduce(
      (acc, feed) => ({
        pendingCount: acc.pendingCount + Number(feed.pendingProductsCount),
        embeddingCount:
          acc.embeddingCount + Number(feed.embeddingProductsCount),
        doneCount: acc.doneCount + Number(feed.doneProductsCount),
        failedCount: acc.failedCount + Number(feed.failedProductsCount),
      }),
      { pendingCount: 0, embeddingCount: 0, doneCount: 0, failedCount: 0 },
    );
  }, [allFeeds]);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addExistingOpen, setAddExistingOpen] = useState(false);
  const [settingsFeedId, setSettingsFeedId] = useState<string | undefined>();

  const handleOpenSettings = useCallback((feedId: string) => {
    setSettingsFeedId(feedId);
    setSettingsOpen(true);
  }, []);

  const handleOpenCreate = useCallback(() => {
    setSettingsFeedId(undefined);
    setSettingsOpen(true);
  }, []);

  const columns = useFeedsCols({ chatId, onOpenSettings: handleOpenSettings });

  const handleQuery = useTableQuery(trpcClient.feeds.findAll.query, {
    where: {
      chats: chatId,
    },
    populate: [
      'chats',
      'productsCount',
      'pendingProductsCount',
      'embeddingProductsCount',
      'doneProductsCount',
      'failedProductsCount',
      'health',
    ],
  });

  const handleRow = useDetailRowHandler<FeedType>(row => ({
    to: '/chats/$chatId/feeds/$feedId/products',
    params: {
      chatId,
      feedId: row.original.id,
    },
  }));

  return (
    <>
      <TablePage
        title={t`Product feeds`}
        breadcrumbs={[
          {
            label: data.name,
            to: '/chats/$chatId/edit',
            params: { chatId },
          },
          {
            label: t`Product feeds`,
            to: '/chats/$chatId/feeds',
            params: { chatId },
          },
        ]}
        actions={
          <div className='flex gap-2'>
            <Button
              data-cta='admin-feed-add-existing-button'
              variant='primary'
              outline
              onClick={() => setAddExistingOpen(true)}
            >
              <Link2 className='size-4' /> {t`Add existing feed`}
            </Button>
            <Button
              data-cta='admin-feed-create-button'
              variant='primary'
              onClick={handleOpenCreate}
            >
              <PlusCircle className='size-4' /> {t`Create product feed`}
            </Button>
          </div>
        }
        refetchInterval={5000}
        columns={columns}
        queryKey={[...getQueryKey(trpc.feeds.findAll), { chatId }]}
        onQueryFn={handleQuery}
        onRow={handleRow}
      >
        <ProductStatsSummary
          pendingCount={aggregateCounts.pendingCount}
          embeddingCount={aggregateCounts.embeddingCount}
          doneCount={aggregateCounts.doneCount}
          failedCount={aggregateCounts.failedCount}
        />
      </TablePage>

      <FeedSettingsModal
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        chatId={chatId}
        feedId={settingsFeedId}
      />

      <AddExistingFeedDialog
        open={addExistingOpen}
        onOpenChange={setAddExistingOpen}
        chatId={chatId}
      />
    </>
  );
}
